/**
 * Kênh sự kiện của người đang đăng nhập: `GET /api/v1/users/me/events` qua
 * Gateway, dạng Server-Sent Events.
 *
 * Không dùng `EventSource` vì nó không gắn được header `Authorization`; đọc
 * luồng bằng `fetch` + `ReadableStream`, tách khung ở `khung-sse.ts`.
 *
 * Hành vi:
 * - mỗi lần mở (kể cả nối lại) lấy access token mới qua `getAccessToken()`;
 * - đứt luồng, 404, 5xx, mất mạng → nối lại sau 1s, 2s, 4s… tối đa 30s, cộng
 *   nhiễu ngẫu nhiên để các tab không cùng nối lại một lúc; nhận được `ready`
 *   thì nhịp giãn cách về lại 1s;
 * - quá 45 giây không nhận byte nào (máy chủ gửi `: ping` mỗi 15 giây) coi như
 *   đứt, cắt luồng và nối lại;
 * - 401 → báo `onHetPhien` để kiểm phiên thật, rồi thử lại; 401 liên tiếp ba
 *   lần thì dừng hẳn, không nối lại vô hạn;
 * - 403 (cổng nền tảng không có tenant, tài khoản bị khoá, tổ chức tạm ngưng)
 *   → dừng im lặng; các màn vẫn còn cơ chế tải lại định kỳ.
 *
 * Không ghi gì ra console: backend chưa sẵn thì luồng chỉ thử lại thưa dần.
 */
import { getAccessToken } from '../auth/oidc';
import { BoTachKhungSse, docSuKien } from './khung-sse';
import type { KhungSse, SuKienNguoiDung } from './khung-sse';

export type { SuKienNguoiDung } from './khung-sse';

const DUONG_DAN = '/api/v1/users/me/events';
const GIAN_CACH_DAU_MS = 1_000;
const GIAN_CACH_TOI_DA_MS = 30_000;
const IM_LANG_TOI_DA_MS = 45_000;
const SO_LAN_401_TOI_DA = 3;

/**
 * - `dangKetNoi`: đang mở hoặc đã mở nhưng chưa nhận `ready`;
 * - `trucTiep`: đã nhận `ready`, sự kiện đang tới;
 * - `matKetNoi`: đứt, đang chờ nối lại;
 * - `dung`: không nối lại nữa (403, 401 lặp lại, hoặc đã đóng).
 */
export type TrangThaiKenh = 'dangKetNoi' | 'trucTiep' | 'matKetNoi' | 'dung';

export interface TuyChonKenh {
  onSuKien: (suKien: SuKienNguoiDung) => void;
  onTrangThai: (trangThai: TrangThaiKenh) => void;
  /** Luồng bị từ chối 401: kiểm phiên thật thay vì đoán. */
  onHetPhien: () => void;
}

class LoiDung extends Error {}
class LoiHetPhien extends Error {}

function choGianCach(lanThu: number): number {
  const goc = Math.min(GIAN_CACH_TOI_DA_MS, GIAN_CACH_DAU_MS * 2 ** lanThu);
  // Nhiễu ±20%, vẫn không vượt trần.
  return Math.min(GIAN_CACH_TOI_DA_MS, Math.round(goc * (0.8 + Math.random() * 0.4)));
}

/** Mở kênh; trả hàm đóng. Gọi hàm đóng nhiều lần vô hại. */
export function moKenhSuKien(tuyChon: TuyChonKenh): () => void {
  let daDong = false;
  let dieuKhien: AbortController | null = null;
  let henGio: ReturnType<typeof setTimeout> | undefined;
  let lanThu = 0;
  let so401LienTiep = 0;
  let idGanNhat: string | undefined;

  const datTrangThai = (t: TrangThaiKenh) => {
    if (!daDong) tuyChon.onTrangThai(t);
  };

  const ngu = (ms: number) =>
    new Promise<void>((xong) => {
      henGio = setTimeout(xong, ms);
    });

  async function motLan(): Promise<void> {
    const ac = new AbortController();
    dieuKhien = ac;

    let token: string | undefined;
    try {
      token = await getAccessToken();
    } catch {
      throw new LoiHetPhien();
    }
    if (!token) throw new LoiDung();

    const headers = new Headers({ Accept: 'text/event-stream', Authorization: `Bearer ${token}` });
    if (idGanNhat) headers.set('Last-Event-ID', idGanNhat);

    const res = await fetch(DUONG_DAN, { headers, signal: ac.signal, cache: 'no-store' });
    if (res.status === 401) throw new LoiHetPhien();
    if (res.status === 403) throw new LoiDung();
    // 200 mà không phải event-stream: `/api` chưa định tuyến, nhận về `index.html`.
    if (!res.ok || !res.body || !(res.headers.get('Content-Type') ?? '').includes('text/event-stream')) {
      void res.body?.cancel().catch(() => undefined);
      throw new Error('kenh-khong-mo-duoc');
    }
    so401LienTiep = 0;

    const doc = res.body.getReader();
    const giaiMa = new TextDecoder();
    const boTach = new BoTachKhungSse();
    let canhGac: ReturnType<typeof setTimeout> | undefined;
    const datCanhGac = () => {
      clearTimeout(canhGac);
      canhGac = setTimeout(() => ac.abort(), IM_LANG_TOI_DA_MS);
    };
    datCanhGac();

    try {
      for (;;) {
        const { done, value } = await doc.read();
        if (done) return;
        datCanhGac();
        for (const khung of boTach.nap(giaiMa.decode(value, { stream: true }))) {
          if (khung.id !== undefined) idGanNhat = khung.id;
          const suKien = docSuKien(khung);
          if (!suKien || daDong) continue;
          if (suKien.type === 'ready') {
            lanThu = 0;
            datTrangThai('trucTiep');
          }
          tuyChon.onSuKien(suKien);
        }
      }
    } finally {
      clearTimeout(canhGac);
    }
  }

  async function chay(): Promise<void> {
    while (!daDong) {
      datTrangThai('dangKetNoi');
      try {
        await motLan();
      } catch (err) {
        if (daDong) return;
        if (err instanceof LoiDung) {
          datTrangThai('dung');
          return;
        }
        if (err instanceof LoiHetPhien) {
          so401LienTiep += 1;
          tuyChon.onHetPhien();
          if (so401LienTiep >= SO_LAN_401_TOI_DA) {
            datTrangThai('dung');
            return;
          }
        }
        // Còn lại: mất mạng, 404, 5xx, quá hạn im lặng — thử lại có giãn cách.
      }
      if (daDong) return;
      datTrangThai('matKetNoi');
      await ngu(choGianCach(lanThu));
      lanThu += 1;
    }
  }

  void chay();

  return () => {
    if (daDong) return;
    daDong = true;
    clearTimeout(henGio);
    dieuKhien?.abort();
  };
}

/**
 * Một luồng SSE mở bằng **POST** (hỏi–đáp của trợ lí thẩm định, đặc tả mục 10.5).
 *
 * Khác `moKenhSuKien`: đường này là một lượt hỏi, không phải kênh thường trực — nên **không nối lại** khi
 * mất kết nối. Nối lại một lượt hỏi là hỏi lại từ đầu và tiêu thêm một lượt gọi mô hình, trong khi câu trả
 * lời dở người dùng đang đọc thì mất.
 *
 * Hàm trả về một `AsyncGenerator` các khung đã tách, và một hàm `dung()` để huỷ. Đóng tab hay bấm Dừng thì
 * `AbortController` ngắt `fetch`, server thấy `close` và dừng lượt gọi mô hình.
 */
export function moLuongPost(duongDan: string, than: unknown): {
  khung: AsyncGenerator<KhungSse>;
  dung: () => void;
} {
  const ac = new AbortController();
  async function* doc(): AsyncGenerator<KhungSse> {
    let token: string | undefined;
    try {
      token = await getAccessToken();
    } catch {
      throw new LoiHetPhien();
    }
    if (!token) throw new LoiDung();

    const res = await fetch(duongDan, {
      method: 'POST',
      headers: new Headers({
        Accept: 'text/event-stream',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      }),
      body: JSON.stringify(than),
      signal: ac.signal,
      cache: 'no-store',
    });
    if (res.status === 401) throw new LoiHetPhien();
    if (res.status === 403) throw new LoiDung();
    /*
     * Lỗi TRƯỚC khi luồng mở còn là JSON problem+json: đọc nó để hiện câu lỗi thật, thay vì một câu chung
     * chung. Sau khi luồng mở thì lỗi đi vào gói `error` trong luồng (đặc tả mục 9.6).
     */
    if (!res.ok || !res.body) {
      let cau = '';
      try {
        const d = (await res.json()) as { detail?: string };
        cau = d.detail ?? '';
      } catch { /* không phải JSON */ }
      throw new Error(cau || `luong-khong-mo-duoc-${res.status}`);
    }

    const tach = new BoTachKhungSse();
    const doc2 = new TextDecoder();
    const reader = res.body.getReader();
    try {
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        for (const k of tach.nap(doc2.decode(value, { stream: true }))) yield k;
      }
      /*
       * Khung dở ở cuối luồng **bỏ qua**: một gói JSON bị cắt giữa chừng không đọc được, và `done` của ta
       * luôn là một khung trọn — luồng dừng giữa khung nghĩa là kết nối đứt, không phải có dữ liệu để đọc.
       */
    } finally {
      reader.releaseLock();
    }
  }
  return { khung: doc(), dung: () => ac.abort() };
}
